import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { adjustInventoryStock } from "./inventory";

const STORAGE_KEY = "almoune_purchases";

export const purchaseStatusOptions = [
  { value: "draft", label: "Brouillon" },
  { value: "ordered", label: "Commande" },
  { value: "partial", label: "Recu partiellement" },
  { value: "received", label: "Recu integralement" },
  { value: "cancelled", label: "Annule" },
];

export const normalizePurchase = (p) => ({
  id: p.id || `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  orderNumber: p.orderNumber || `ACH-${Date.now()}`,
  supplierId: p.supplierId || "",
  supplierName: p.supplierName || "",
  status: p.status || "draft",
  items: Array.isArray(p.items) ? p.items.map((item) => ({
    id: item.id || `pitem-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    productId: item.productId || "",
    productName: item.productName || "",
    quantityOrdered: Number(item.quantityOrdered || 0),
    quantityReceived: Number(item.quantityReceived || 0),
    unitCost: Number(item.unitCost || 0),
    lineTotal: Number((Number(item.quantityOrdered || 0) * Number(item.unitCost || 0)).toFixed(2)),
  })) : [],
  totalAmount: Number(p.totalAmount || 0),
  notes: p.notes || "",
  expectedDate: p.expectedDate || null,
  receivedDate: p.receivedDate || null,
  createdAt: p.createdAt || new Date().toISOString(),
});

const readCache = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizePurchase) : [];
  } catch {
    return [];
  }
};

const writeCache = (purchases) => {
  const normalized = purchases.map(normalizePurchase);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

export const getPurchases = () => readCache();

export const loadPurchases = async () => {
  const cached = readCache();
  if (!isSupabaseConfigured || !supabase) return cached;
  try {
    const { data, error } = await supabase.from("purchases").select("*").order("created_at", { ascending: false });
    if (error || !data) return cached;
    return writeCache(data.map((row) => ({
      id: row.id,
      orderNumber: row.order_number,
      supplierId: row.supplier_id,
      supplierName: row.supplier_name,
      status: row.status,
      items: row.items || [],
      totalAmount: row.total_amount,
      notes: row.notes,
      expectedDate: row.expected_date,
      receivedDate: row.received_date,
      createdAt: row.created_at,
    })));
  } catch {
    return cached;
  }
};

export const savePurchase = async (purchase) => {
  const normalized = normalizePurchase(purchase);
  const all = readCache();
  const idx = all.findIndex((p) => p.id === normalized.id);
  normalized.totalAmount = normalized.items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
  const updated = idx >= 0 ? all.map((p, i) => (i === idx ? normalized : p)) : [...all, normalized];
  const saved = writeCache(updated);
  if (!isSupabaseConfigured || !supabase) return saved;
  try {
    await supabase.from("purchases").upsert({
      id: normalized.id,
      order_number: normalized.orderNumber,
      supplier_id: normalized.supplierId,
      supplier_name: normalized.supplierName,
      status: normalized.status,
      items: normalized.items,
      total_amount: normalized.totalAmount,
      notes: normalized.notes,
      expected_date: normalized.expectedDate,
      received_date: normalized.receivedDate,
      created_at: normalized.createdAt,
    }, { onConflict: "id" });
  } catch {
    // cache already written
  }
  return saved;
};

export const deletePurchase = async (id) => {
  const all = readCache();
  const updated = all.filter((p) => p.id !== id);
  writeCache(updated);
  if (!isSupabaseConfigured || !supabase) return true;
  try {
    const { error } = await supabase.from("purchases").delete().eq("id", id);
    if (error) throw error;
  } catch (err) {
    throw err;
  }
  return true;
};

/**
 * Receive items from a purchase order and update stock.
 * Returns the updated purchase order.
 */
export const receivePurchaseItems = async (purchaseId, receivedQuantities) => {
  const all = readCache();
  const purchase = all.find((p) => p.id === purchaseId);
  if (!purchase) throw new Error("Commande d'approvisionnement introuvable.");

  const updatedItems = purchase.items.map((item) => {
    const received = Number(receivedQuantities[item.id] || 0);
    if (received <= 0) return item;
    const newQtyReceived = Number(item.quantityReceived || 0) + received;
    return {
      ...item,
      quantityReceived: Math.min(newQtyReceived, item.quantityOrdered),
    };
  });

  const allReceived = updatedItems.every((item) => item.quantityReceived >= item.quantityOrdered);
  const anyReceived = updatedItems.some((item) => item.quantityReceived > 0);

  const updatedPurchase = {
    ...purchase,
    items: updatedItems,
    status: allReceived ? "received" : anyReceived ? "partial" : purchase.status,
    receivedDate: allReceived ? new Date().toISOString() : purchase.receivedDate,
  };

  for (const item of updatedItems) {
    const received = Number(receivedQuantities[item.id] || 0);
    if (received > 0 && item.productId) {
      await adjustInventoryStock(
        item.productId,
        received,
        `Reception commande ${purchase.orderNumber} - ${item.productName}`
      );
    }
  }

  return await savePurchase(updatedPurchase);
};

/**
 * Dernier prix d'achat connu par produit (source: historique des achats).
 * Le prix d'achat n'est pas stable (ex: tomates 1,90 EUR/kg puis 3,20 EUR/kg):
 * la ligne la plus recente gagne. Retourne une Map<productId, { unitCost, date }>.
 */
export const getLastPurchaseCostMap = (purchases) => {
  const latest = new Map();
  (Array.isArray(purchases) ? purchases : []).forEach((purchase) => {
    if (!purchase) return;
    const date =
      purchase.createdAt ||
      purchase.receivedDate ||
      "";
    (Array.isArray(purchase.items) ? purchase.items : []).forEach((item) => {
      const cost = Number(item.unitCost || 0);
      if (!item.productId || !(cost > 0)) return;
      const previous = latest.get(item.productId);
      if (!previous || new Date(date) > new Date(previous.date)) {
        latest.set(item.productId, { unitCost: cost, date });
      }
    });
  });
  return latest;
};

/**
 * Generate supplier order requests (draft purchases) from low stock products.
 * Groups products by supplierId and creates one draft purchase per supplier.
 * Returns array of created purchase orders.
 */
export const generateSupplierOrdersFromLowStock = async (products, suppliers, threshold = 5) => {
  const lowStockProducts = products.filter((p) => {
    const stock = Number(p.stock || 0);
    return stock <= threshold && stock >= 0;
  });

  if (lowStockProducts.length === 0) return [];

  const purchases = readCache();
  const existingDrafts = purchases.filter((p) => p.status === "draft");

  const supplierMap = new Map();
  for (const product of lowStockProducts) {
    const supplierId = product.supplierId;
    if (!supplierId) continue;
    if (!supplierMap.has(supplierId)) {
      supplierMap.set(supplierId, []);
    }
    supplierMap.get(supplierId).push(product);
  }

  const costMap = getLastPurchaseCostMap(purchases);
  const created = [];

  for (const [supplierId, productsList] of supplierMap.entries()) {
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) continue;

    const existingDraft = existingDrafts.find((p) => p.supplierId === supplierId && p.status === "draft");
    const items = productsList.map((product) => {
      const stock = Number(product.stock || 0);
      const reorderQty = Math.max(10, (threshold - stock) + 10);
      const costInfo = costMap.get(product.id);
      const unitCost = costInfo?.unitCost || 0;
      return {
        productId: product.id,
        productName: product.name,
        quantityOrdered: reorderQty,
        quantityReceived: 0,
        unitCost,
        lineTotal: Number((reorderQty * unitCost).toFixed(2)),
      };
    });

    if (items.length === 0) continue;

    const purchase = {
      id: existingDraft?.id || `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      orderNumber: existingDraft?.orderNumber || `ACH-${Date.now()}`,
      supplierId,
      supplierName: supplier.name,
      status: "draft",
      items,
      totalAmount: items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0),
      notes: `Auto-genere: stock faible (${new Date().toLocaleDateString("fr-FR")})`,
      expectedDate: null,
      receivedDate: null,
      createdAt: existingDraft?.createdAt || new Date().toISOString(),
    };

    await savePurchase(purchase);
    created.push(purchase);
  }

  return created;
};
